<template>
  <DragPanel
    title="水面"
    :closePanel="closePanel"
    :tooltipVisible="false"
    ref="dom">
    <div class="create-water-box">
      <div class="steps-box">
        <div class="step-item-box">
          <div :class="getStepIndexClassName(1)">1</div>
          <div :class="getStepNameClassName(1)">区域绘制</div>
        </div>
        <div class="step-connector-line" />
        <div class="step-item-box">
          <div :class="getStepIndexClassName(2)">2</div>
          <div :class="getStepNameClassName(2)">属性编辑</div>
        </div>
      </div>
      <div class="common-form-box">
        <el-form
          class="public-form-primary"
          ref="waterForm"
          :inline="true"
          :model="waterForm"
          :rules="rules"
          label-width="56px">
          <el-form-item
            class="form-item"
            label="名称："
            prop="waterName">
            <div class="common-input-box">
              <el-input
                class="input-primary"
                size="small"
                :minlength="1"
                :maxlength="20"
                show-word-limit
                v-model="waterForm.waterName"
                placeholder="请输入水面名称" />
            </div>
          </el-form-item>
        </el-form>
      </div>
      <div
        class="first-step-box"
        v-show="currStep === 1">
        <div class="create-mode-box">
          <div class="label-box">
            <div class="name dialog-label">绘制方式</div>
            <el-tooltip
              :effect="$theme.tooltipKey()"
              placement="top-start"
              popper-class="common-panel-tooltip-box">
              <div slot="content">
                <div>
                  我们提供两种创建多边形区域的方法：手动绘制和从文件导入。
                </div>
                <div>
                  (1)
                  手动绘制区域：您可以在画布上用鼠标点击来确定多边形的顶点，系统会自动连接这些点形成区域
                </div>
                <div>
                  (2)
                  导入区域文件：如果您已有包含<span>单个多边形</span>顶点信息的文件(支持json或geojson格式),可直接导入，系统将自动解析并显示对应的多边形。
                </div>
                <div>两种方式都能帮助您便捷地创建所需的多边形区域</div>
              </div>
              <div class="icon-box">
                <icon-svg
                  icon-class="ic-jieshishuoming"
                  size="14" />
              </div>
            </el-tooltip>
            <div>:</div>
          </div>
          <div class="common-button-box">
            <el-button
              class="primary-btn gr8"
              size="small"
              type="primary"
              :class="currMode === 'create' ? 'active' : ''"
              @click="createWaterByCursor">
              手动绘制区域
            </el-button>
            <ReadFileFromLocal
              :disabled="createSource === 'fromCursor'"
              :handleUploadBtn="handleUploadBtn"
              :onFileLoaded="handleFileLoaded" />
          </div>
        </div>
        <div class="uniform-height-box">
          <div class="label-box">
            <div class="name dialog-label">统一顶点高度</div>
            <el-tooltip
              :effect="$theme.tooltipKey()"
              placement="top-start"
              popper-class="common-panel-tooltip-box">
              <div slot="content">
                在绘制矢量面时，由于手动操作可能存在偏差，各顶点可能出现高低不平的情况。为确保图形平整，您可在完成绘制后使用高度统一功能，一键将所有顶点智能对齐到相同水平面，消除高度误差
              </div>
              <div class="icon-box">
                <icon-svg icon-class="ic-jieshishuoming" />
              </div>
            </el-tooltip>
            <div>:</div>
          </div>
          <div
            :class="`icon-btn-box ${isPickUpHeightEnable ? 'active' : ''}`"
            @click="pickUpHeight">
            <icon-svg icon-class="icon-shiqubingtongyidingdiangaodu" />
          </div>
        </div>
      </div>
      <div
        class="last-step-box"
        v-show="currStep === 2">
        <div class="basic-param-box">
          <div class="color-box">
            <div class="label-box dialog-label">颜色：</div>
            <ColorPicker
              ref="colorPicker"
              :defaultColor="waterColor"
              :recentColorList="recentColorList"
              :recommendColorList="recommendColorList"
              :onColorChange="onColorPickerChange" />
          </div>
          <div class="opacity-box">
            <div class="label-box dialog-label">水面透明度：</div>
            <div class="opacity-slider-box">
              <div class="slider-box">
                <el-slider
                  input-size="small"
                  v-model="waterOpacity"
                  :max="1"
                  :step="0.1"
                  :show-tooltip="false"
                  @change="onColorPickerChange" />
              </div>
              <div class="slider-value dialog-label">{{ waterOpacity }}</div>
            </div>
          </div>
        </div>
        <div class="senior-param-box">
          <div
            class="title-box"
            @click="toggleSeniorParam">
            <div class="dialog-label">高级参数</div>
            <div class="icon-box">
              <i
                class="el-icon-arrow-up"
                v-if="isSeniorParamsVisible" />
              <i
                class="el-icon-arrow-down"
                v-else />
            </div>
          </div>
          <div
            class="param-content-box"
            v-if="isSeniorParamsVisible">
            <div class="input-item-box">
              <div class="item-box">
                <div class="label-box">可视距离：</div>
                <div class="common-input-box">
                  <el-input-number
                    size="small"
                    :controls="false"
                    v-model="visDist"
                    @change="value => onSeniorParamChange('visDist', value)" />
                </div>
                <div class="unit-box">km</div>
              </div>
              <div class="item-box">
                <div class="label-box">外扩：</div>
                <div class="common-input-box">
                  <el-input-number
                    size="small"
                    :controls="false"
                    v-model="expandDist"
                    @change="
                      value => onSeniorParamChange('expandDist', value)
                    " />
                </div>
                <div class="unit-box">m</div>
              </div>
            </div>
            <div class="slider-item-box">
              <div class="item-box">
                <div class="label-box">混合系数：</div>
                <el-slider
                  class="slider-primary"
                  input-size="small"
                  v-model="blendDist"
                  :min="0"
                  :max="1"
                  :step="0.1"
                  @change="value => onSeniorParamChange('blendDist', value)" />
              </div>
            </div>
            <div class="slider-item-box">
              <div class="item-box">
                <div class="label-box">深度偏移：</div>
                <el-slider
                  class="slider-primary"
                  input-size="small"
                  v-model="depthBias"
                  :min="-0.0001"
                  :max="0.0001"
                  :step="0.00001"
                  @change="value => onSeniorParamChange('depthBias', value)" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="folder-path-box">
        <div class="label-box dialog-label">目标路径：</div>
        <div
          class="muti-line-text-overflow dialog-label"
          :title="getFolderPathName()">
          {{ getFolderPathName() }}
        </div>
      </div>
    </div>
    <div class="operation-box">
      <div
        class="common-button-box"
        v-if="currStep === 1">
        <el-button
          size="small"
          class="normal-btn gr8 primary-ang-btn"
          @click="clearReg">
          清除区域
        </el-button>
        <el-button
          size="small"
          class="primary-btn gr8 primary-ang-btn"
          type="primary"
          @click="nextStep">
          下一步
        </el-button>
      </div>
      <div
        class="common-button-box btn-group-primary"
        v-if="currStep === 2">
        <el-button
          size="small"
          class="normal-btn prev-btn gr8 primary-ang-btn"
          @click="lastStep">
          上一步
        </el-button>
        <el-button
          size="small"
          class="primary-btn primary-ang-btn"
          type="primary"
          @click="saveWater">
          保存
        </el-button>
      </div>
    </div>
  </DragPanel>
</template>

<script>
import * as turf from '@turf/turf'

import DragPanel from '@/components/CustomDragPanel.vue'
import ColorPicker from '@/components/ColorPicker.vue'
import ReadFileFromLocal from '@/components/ReadFileFromLocal.vue'

import { pad2 } from '@/utils/generalMethods'

import {
  createWaterService,
  getWaterColorsService,
  getSingleSceneTreeById
} from '@/service/interface.js'

export default {
  data() {
    const validWaterNameRule = (rule, value, callback) => {
      this.isSameNameCheck(value).then(isCheckPass => {
        if (!isCheckPass) {
          callback(new Error('当前场景中存在同名水面对象，请指定其它名称'))
          return
        }

        if (value.length !== value.trimStart().trimEnd().length) {
          callback(new Error('开头和结尾不允许有空格'))
          return
        }

        callback()
      })
    }

    return {
      currStep: 1,
      activeSteps: [1],
      // 原始水面名称，用于判断是否修改了水面名称
      originWaterName: '',
      waterForm: {
        waterName: ''
      },
      waterID: '',
      rules: {
        waterName: [
          {
            required: true,
            message: '请输入水面名称',
            trigger: 'blur'
          },
          {
            min: 1,
            max: 20,
            message: '输入内容请控制在1到20个字符之间',
            trigger: 'change'
          },
          { validator: validWaterNameRule, trigger: 'blur' }
        ]
      },

      // 创建来源(fromCursor: 鼠标绘制, fromFile: 文件导入)
      createSource: '',
      // 当前模式(create:创建, edit:编辑, preview:预览，clear:清除区域, 瞬时状态，防止因响应REAddWaterRgnFinish事件弹出提示)
      currMode: '',
      isPickUpHeightEnable: false,

      waterColor: '',
      waterOpacity: 0.6,
      recentColorList: [],
      recommendColorList: [],
      isSeniorParamsVisible: true,
      blendDist: 0,
      visDist: 200,
      depthBias: 0,
      expandDist: 0,

      // 当前选中的矢量边，作为是否给出操作提示的依据
      currElemType: '',
      currEdgeId: '',
      originX: -1,
      originY: -1
    }
  },
  props: {
    currDataSetFolder: Object,
    updateSceneTree: Function,
    expandNodeByDataSetType: Function,
    closeWaterPanel: Function
  },
  components: {
    DragPanel,
    ColorPicker,
    ReadFileFromLocal
  },
  mounted() {
    this.initWaterParams()

    // prettier-ignore
    document.addEventListener("REAddWaterRgnFinish", this.onREAddWaterRgnFinish);
    document.addEventListener('RESystemSelElement', this.onSelElement)
    document.addEventListener('RESystemSelShpElement', this.onSelElement)
    this.addCanvasEventListener()

    this.setPanelStyle()
  },
  beforeDestroy() {
    // prettier-ignore
    document.removeEventListener("REAddWaterRgnFinish", this.onREAddWaterRgnFinish);
    document.removeEventListener('RESystemSelElement', this.onSelElement)
    document.removeEventListener('RESystemSelShpElement', this.onSelElement)
    this.removeCanvasEventListener()

    // 组件销毁时清除计时器
    if (this.saveWaterTimer) {
      clearTimeout(this.saveWaterTimer)
    }
  },
  methods: {
    initWaterParams() {
      let waterName = this.getWaterDefaultName()
      this.originWaterName = waterName
      this.waterForm.waterName = waterName
      this.waterID = `Water_${new Date().getTime()}`

      getWaterColorsService().then(response => {
        if (response.isSuccess) {
          let data = response.data
          let recentColorList = data.commonColors
          let recommendColorList = data.intrinsicColors
          if (recentColorList.length) {
            this.waterColor = recentColorList[0]
          } else {
            this.waterColor = recommendColorList[0]
          }

          this.recentColorList = recentColorList
          this.recommendColorList = recommendColorList
        } else {
          this.$message.error(response.errMsg)
        }
      })
    },
    createWaterByCursor() {
      this.$refs.waterForm.validate((bool, object) => {
        if (bool) {
          if (this.currMode !== '') {
            this.$message.warning('请先清除当前绘制区域，再进行新的绘制操作')
            return
          }

          let camLoc = window.BlackHole3D.Camera.getCamLocate()
          let camPos = camLoc.camPos
          let camHeight = camPos[2]
          if (camHeight > 100000) {
            // prettier-ignore
            this.$message.warning("当前相机高度超过100公里，无法进行打点操作。请降低相机高度后重试。");
            return
          }

          const Water = window.BlackHole3D.Water

          this.currMode = 'create'
          this.createSource = 'fromCursor'
          this.setWaterDefaultAttr()

          Water.startEditState()
          Water.startAddWaterState(this.waterID)
        } else {
          // prettier-ignore
          this.$message.error("表单验证失败，请正确填写相关内容后再进行绘制操作");
        }
      })
    },
    handleUploadBtn() {
      this.setWaterDefaultAttr()
    },
    handleFileLoaded(fileContent) {
      if (!fileContent) return

      this.drawWaterByFile(fileContent)

      const Water = window.BlackHole3D.Water
      Water.startEditState()
      Water.setShowState([this.waterID], 0)

      Water.setCamToData([this.waterID])
      this.createSource = 'fromFile'
      this.currMode = 'edit'
    },
    pickUpHeight() {
      if (!this.isValidRegionExist()) {
        this.$message.warning('请先完成有效区域的绘制，再设置统一高度')
        return
      }

      this.isPickUpHeightEnable = true
    },
    clearReg() {
      if (this.currMode === '') {
        this.$message.warning('当前没有可清除的区域，请先进行区域绘制')
        return
      }

      if (!this.isValidRegionExist()) {
        this.$message.warning('请先完成当前区域绘制后，再点击清除区域')
        return
      }

      let warningMsg = '此操作将会清除当前选定区域，是否继续？'
      this.$confirm(warningMsg, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          this.currMode = 'clear'

          const Water = window.BlackHole3D.Water

          let waterID = this.waterID

          Water.endAddWaterState()
          Water.endEditState()

          Water.delData([waterID])

          this.isPickUpHeightEnable = false
          let coorInfo = { visible: false }
          this.$eventManage.fire('canvasMouseMove', { coorInfo })

          if (this.createSource == 'fromCursor') {
            this.createSource = 'fromCursor'
            this.currMode = 'create'

            Water.startEditState()
            Water.startAddWaterState(this.waterID)
          } else if (this.createSource == 'fromFile') {
            this.createSource = ''
            this.currMode = ''
          }
        })
        .catch(() => {
          this.$message.info('取消清除')
        })
    },
    nextStep() {
      this.$refs.waterForm.validate((bool, object) => {
        if (bool) {
          if (this.currMode === '') {
            this.$message.warning('当前没有可编辑的区域，请先进行区域绘制')
            return
          }

          if (this.currMode === 'create') {
            this.$message.warning('请先完成当前区域绘制，再点击下一步')
            return
          }

          const Water = window.BlackHole3D.Water
          Water.endAddWaterState()
          Water.endEditState()
          this.currMode = 'preview'

          this.currStep = 2
          if (!this.activeSteps.includes(2)) {
            this.activeSteps.push(2)
          }

          this.setPanelStyle()
        } else {
          // prettier-ignore
          this.$message.error("表单验证失败，请正确填写相关内容后再保存");
        }
      })
    },
    closePanel() {
      return new Promise(resolve => {
        this.isUnSaveCheck().then(isCheckPass => {
          if (isCheckPass) {
            this.closeWaterPanel()
            resolve()
            return
          }

          this.$confirm('您编辑的内容未被保存，确定要退出吗？', '提示', {
            confirmButtonText: '保存并退出',
            cancelButtonText: '直接退出',
            distinguishCancelAndClose: true,
            showConfirmButton: this.isValidRegionExist(),
            type: 'warning'
          })
            .then(() => {
              if (!this.isValidRegionExist()) {
                this.exitCreateWater(true)
                this.closeWaterPanel()
                resolve()
                return
              }

              this.createWater().then(() => {
                this.updateSceneTree().then(() => {
                  this.expandNodeByDataSetType(23)
                  this.exitCreateWater(false)
                  this.$message.success('保存成功')
                  this.closeWaterPanel()
                  resolve()
                  return
                })
              })
            })
            .catch(e => {
              if (e === 'cancel') {
                this.exitCreateWater(true)
                this.closeWaterPanel()
                resolve()
                return
              }
            })
        })
      })
    },
    exitCreateWater(isRemoveWater) {
      // 清除当前模式，防止因调用endAddWaterState触发REAddWaterRgnFinish事件导致的异常
      this.currMode = ''
      this.createSource = ''

      const Water = window.BlackHole3D.Water

      let waterID = this.waterID

      Water.endAddWaterState()
      Water.endEditState()

      if (isRemoveWater) {
        Water.delData([waterID])
      }

      let coorInfo = { visible: false }
      this.$eventManage.fire('canvasMouseMove', { coorInfo })
    },
    isUnSaveCheck() {
      return new Promise(resolve => {
        let isCheckPass = true
        if (this.currMode !== '' && this.currMode !== 'clear') {
          isCheckPass = false
        }
        // prettier-ignore
        let isNameChange = this.originWaterName !== this.waterForm.waterName;
        if (isNameChange) {
          isCheckPass = false
        }

        resolve(isCheckPass)
      })
    },
    onColorPickerChange() {
      this.setWaterColor(this.waterID)
    },
    lastStep() {
      const Water = window.BlackHole3D.Water
      Water.startEditState()
      Water.setShowState([this.waterID], 0)
      this.currMode = 'edit'

      this.currStep = 1
      this.setPanelStyle()
    },
    saveWater() {
      let startInterval = setInterval(() => {
        this.isSaveDisabled = false
        clearInterval(startInterval)
      }, 10000)

      if (!this.isSaveDisabled) {
        this.$refs.waterForm.validate((bool, object) => {
          if (bool) {
            this.createWater().then(() => {
              this.updateSceneTree().then(() => {
                this.expandNodeByDataSetType(23)
                this.isSaveDisabled = false
                this.$message.success('保存成功')
                this.closeWaterPanel()
              })
            })
          } else {
            // prettier-ignore
            this.$message.error("表单验证失败，请正确填写相关内容后进行下一步操作");
          }
        })

        this.isSaveDisabled = true
      }
    },
    createWater() {
      return new Promise(resolve => {
        const Water = window.BlackHole3D.Water
        let sceneId = this.$route.query.sceneId
        let domRef = this.$refs.colorPicker
        let color = domRef.getHexColor()
        let waterData = Water.getData([this.waterID])
        let waterInfo = waterData[0]
        let transparency = Number((1 - this.waterOpacity).toFixed(1))
        let parentId = this.getParentId()

        let params = {
          sceneId,
          parentId,
          name: this.waterForm.waterName,
          color,
          transparency,
          geojson: JSON.stringify(waterInfo),
          blendDist: this.blendDist,
          expandDist: this.expandDist,
          depthBias: this.depthBias,
          visDist: this.visDist * 1000
        }
        createWaterService(params).then(response => {
          if (response.isSuccess) {
            let data = response.data
            Water.delData([this.waterID])
            waterData[0].waterName = data.id
            this.waterID = data.id

            Water.setData(waterData)
            resolve(response)
          } else {
            this.$message.error(response.errMsg)
          }
        })
      })
    },
    setWaterDefaultAttr() {
      const BlackHole3D = window.BlackHole3D

      let domRef = this.$refs.colorPicker
      // prettier-ignore
      let transparency = Math.round(Number((1 - this.waterOpacity).toFixed(1)) * 255);
      let color = domRef.getColor().concat(transparency)
      // prettier-ignore
      let waterColor = new BlackHole3D.REColor(color[0], color[1], color[2], color[3]);

      let attrInfo = new BlackHole3D.REWaterAttrInfo()
      attrInfo.waterClr = waterColor
      // 混合系数，值越大边缘混合效果越强
      attrInfo.blendDist = this.blendDist
      attrInfo.visible = true
      // 表示水面区域的边界扩充距离
      attrInfo.expandDist = this.expandDist
      // 表示水面的深度偏移
      attrInfo.depthBias = this.depthBias
      // 表示水面的最远可视距离
      attrInfo.visDist = this.visDist * 1000

      BlackHole3D.Water.setDefaultAttr(attrInfo)
    },
    setWaterColor(waterID) {
      const BlackHole3D = window.BlackHole3D

      let domRef = this.$refs.colorPicker
      // prettier-ignore
      let transparency = Math.round(Number((1 - this.waterOpacity).toFixed(1)) * 255);
      let color = domRef.getColor().concat(transparency)
      // prettier-ignore
      let waterColor = new BlackHole3D.REColor(color[0], color[1], color[2], color[3]);

      BlackHole3D.Water.setClr(waterID, waterColor)
    },
    checkFile(fileContent) {
      let isCheckPass = true
      if (!fileContent) {
        isCheckPass = false
        return
      }

      let features = fileContent.features
      if (features.length > 1) {
        this.$message.error('导入失败，因为该文件包含多个矢量要素')
        isCheckPass = false
        return isCheckPass
      }

      let feature = features[0]
      let geometry = feature.geometry

      // 目前多面在引擎层面支持，但业务层面的还没有设计好，因此暂不支持
      if (this.isMutiPolygon(geometry)) {
        this.$message.error('导入失败，因为该文件包含多个区域')
        isCheckPass = false
        return isCheckPass
      }

      if (this.isPolygonCreationFailed(geometry)) {
        // prettier-ignore
        this.$message.error("导入失败，该文件区域读取失败，读取的点无法组成一个闭合矢量面");
        isCheckPass = false
        return isCheckPass
      }

      return isCheckPass
    },
    isMutiPolygon(geometry) {
      let geometryType = geometry.type
      return geometryType.includes('Polygon') && geometryType !== 'Polygon'
    },
    isPolygonCreationFailed(geometry) {
      let geometryType = geometry.type

      let isFailed = false

      if (geometryType !== 'Polygon') {
        isFailed = true
        return isFailed
      }

      let coordList = geometry.coordinates[0]
      if (coordList.length < 3) {
        isFailed = true
        return isFailed
      }

      isFailed = this.isSelfIntersect(coordList)
      return isFailed
    },
    // 判断是否自相交
    isSelfIntersect(coordList) {
      let polygon = turf.polygon([coordList])
      let kinks = turf.kinks(polygon)

      let features = kinks.features

      let isIntersect = features.length ? true : false
      return isIntersect
    },
    drawWaterByFile(fileContent) {
      // 校验文件内容合法性
      let isCheckPass = this.checkFile(fileContent)
      if (!isCheckPass) return

      let sourceDataCrs = this.getSourceDataCrs(fileContent)
      let sourceCoordList = this.getCoordList(fileContent)

      // prettier-ignore
      let transformedCoordList = this.getTransformedCoordList(sourceCoordList, sourceDataCrs);

      const BlackHole3D = window.BlackHole3D
      let cornerRgnInfo = new BlackHole3D.RECornerRgnInfo()
      cornerRgnInfo.pointList = transformedCoordList

      let domRef = this.$refs.colorPicker
      // prettier-ignore
      let transparency = Math.round(Number((1 - this.waterOpacity).toFixed(1)) * 255);
      let color = domRef.getColor().concat(transparency)
      // prettier-ignore
      let waterColor = new BlackHole3D.REColor(color[0], color[1], color[2], color[3]);

      let waterInfo = new BlackHole3D.REWaterInfo()

      waterInfo.waterName = this.waterID
      waterInfo.waterClr = waterColor
      waterInfo.visible = true
      // 混合系数，值越大边缘混合效果越强
      waterInfo.blendDist = this.blendDist
      // 表示水面区域的边界扩充距离
      waterInfo.expandDist = this.expandDist
      // 表示水面的深度偏移
      waterInfo.depthBias = this.depthBias
      // 表示水面的最远可视距离
      waterInfo.visDist = this.visDist * 1000
      waterInfo.rgnList = [cornerRgnInfo]

      BlackHole3D.Water.setData([waterInfo])
    },
    getSourceDataCrs(fileContent) {
      let sourceDataCrs = 'EPSG:4326'

      let features = fileContent.features
      let feature = features[0]

      let properties = feature.properties
      if (properties && properties.crs) {
        sourceDataCrs = properties.crs
      }

      return sourceDataCrs
    },
    getCoordList(fileContent) {
      let coordList = []
      let features = fileContent.features
      let feature = features[0]
      let geometry = feature.geometry
      let properties = feature.properties

      if (geometry.type === 'Polygon') {
        coordList = geometry.coordinates[0]
        coordList = coordList.map((item, index) => {
          if (properties.heights && properties.heights.length) {
            let hight = properties.heights[index]
            item.push(hight)
          } else {
            item.push(0)
          }

          return item
        })
      }

      return coordList
    },
    getTransformedCoordList(sourceCoordList, sourceDataCrs) {
      let transformedCoordList = []

      const Coordinate = window.BlackHole3D.Coordinate
      let engineWorldCrs = Coordinate.getEngineWorldCRS()

      if (engineWorldCrs) {
        sourceCoordList.forEach(item => {
          // prettier-ignore
          let transformedPoint = Coordinate.getTransEngineCoords(false, sourceDataCrs, [item]);
          transformedCoordList.push(transformedPoint[0])
        })
      } else {
        let destCRS = 'EPSG:3857'
        sourceCoordList.forEach(item => {
          item.push(0)
        })

        // prettier-ignore
        transformedCoordList = Coordinate.getTransGeoCoords(sourceDataCrs, destCRS, sourceCoordList);

        transformedCoordList = transformedCoordList.map(item => {
          item.pop()
          return item
        })
      }

      return transformedCoordList
    },
    isValidRegionExist() {
      let isExist = true
      if (this.currMode === 'create') {
        return false
      }

      const Water = window.BlackHole3D.Water
      let currWaterData = Water.getData([this.waterID])
      if (!currWaterData.length) {
        isExist = false
        return isExist
      }

      let waterInfo = currWaterData[0]
      let rgnList = waterInfo.rgnList
      let rgnInfo = rgnList[0]
      if (rgnInfo.pointList.length < 3) {
        isExist = false
        return isExist
      }

      return isExist
    },
    onREAddWaterRgnFinish() {
      if (this.currMode !== 'create') return

      const Water = window.BlackHole3D.Water

      let errorWaterIds = Water.getErrorDrawWaterIds()
      if (errorWaterIds.includes(this.waterID)) {
        this.handleInvalidPolygon()
        return
      }

      let currWaterData = Water.getData([this.waterID])
      if (!currWaterData.length) {
        this.handleInvalidPolygon()
        return
      }

      this.currMode = 'edit'

      let coorInfo = { visible: false }
      this.$eventManage.fire('canvasMouseMove', { coorInfo })
    },
    handleInvalidPolygon() {
      const Water = window.BlackHole3D.Water
      this.$message.warning('本次绘制未生成有效多边形，请重新绘制')
      Water.delData([this.waterID])
      Water.startEditState()
      Water.startAddWaterState(this.waterID)
    },
    // 处理元素点击事件(包括普通数据和矢量数据点击)，用于拾取高度
    onSelElement(eventInfo) {
      if (this.currMode === 'create') return

      const BlackHole3D = window.BlackHole3D
      let currProbeRet = BlackHole3D.Probe.getCurCombProbeRet()
      let { elemType, elemId } = currProbeRet

      if (this.currEdgeId === elemId) {
        this.currElemType = ''
        this.currEdgeId = ''
        return
      }

      if (elemType === 'ShapeElem') {
        if (elemId && elemId.includes('WATER_EDGE')) {
          // this.$message.info("再次点击选中边可以添加中点");

          this.currElemType = 'WATER_EDGE'
          this.currEdgeId = elemId
        } else {
          this.currElemType = ''
        }
      } else {
        this.currElemType = ''
        this.currEdgeId = ''
      }

      if (!this.isPickUpHeightEnable) return

      // 右键取消拾取高度
      let eventDetail = eventInfo.detail
      if (eventDetail.button === 1) {
        this.isPickUpHeightEnable = false

        let coorInfo = { visible: false }
        this.$eventManage.fire('canvasMouseMove', { coorInfo })
        return
      }

      let currPos = currProbeRet.elemPos
      if (currPos.length) {
        let height = Number(currPos[2].toFixed(2))

        const Water = window.BlackHole3D.Water

        let waterData = Water.getData([this.waterID])
        let waterInfo = waterData[0]
        let rgnList = waterInfo.rgnList
        let cornerRgnInfo = rgnList[0]
        let pointList = cornerRgnInfo.pointList
        pointList.forEach(point => {
          point[2] = height
        })

        Water.setData(waterData)

        Water.endAddWaterState()
        Water.endEditState()

        Water.startEditState()
        Water.setShowState([this.waterID], 0)
      }

      this.isPickUpHeightEnable = false

      let coorInfo = { visible: false }
      this.$eventManage.fire('canvasMouseMove', { coorInfo })
    },
    addCanvasEventListener() {
      let domRef = null
      if (window.BlackHole3D) {
        domRef = window.BlackHole3D.canvas
      } else {
        domRef = document.getElementById('engineCanvas')
      }
      domRef.addEventListener('click', this.onCanvasClick)
      domRef.addEventListener('mouseup', this.onCanvasMouseUp)
      domRef.addEventListener('mousemove', this.onCanvasMouseMove)
      domRef.addEventListener('mouseleave', this.onCanvasMouseLeave)
    },
    removeCanvasEventListener() {
      let domRef = null
      if (window.BlackHole3D) {
        domRef = window.BlackHole3D.canvas
      } else {
        domRef = document.getElementById('engineCanvas')
      }
      domRef.removeEventListener('click', this.onCanvasClick)
      domRef.removeEventListener('mouseup', this.onCanvasMouseUp)
      domRef.removeEventListener('mousemove', this.onCanvasMouseMove)
      domRef.removeEventListener('mouseleave', this.onCanvasMouseLeave)
    },
    onCanvasClick(eventInfo) {
      // 由于canvaClick事件早于RESystemSelShpElement事件触发，因此增加延迟，以获取RESystemSelShpElement事件中拿到的数据
      setTimeout(() => {
        if (this.currElemType === 'WATER_EDGE' && this.currEdgeId) {
          this.originX = eventInfo.clientX
          this.originY = eventInfo.clientY

          let coorInfo = {
            visible: true,
            tipContent: '再次点击选中边可以添加中点',
            clientX: eventInfo.clientX,
            clientY: eventInfo.clientY
          }
          this.$eventManage.fire('canvasMouseMove', { coorInfo })
        } else {
          this.originX = -1
          this.originY = -1
          let coorInfo = { visible: false }
          this.$eventManage.fire('canvasMouseMove', { coorInfo })
        }
      }, 300)
    },
    onCanvasMouseUp(eventInfo) {
      let deletaX = eventInfo.clientX - this.originX
      let deletaY = eventInfo.clientY - this.originY
      if (Math.abs(deletaX) > 5 && Math.abs(deletaY) > 5) {
        this.currElemType = ''
      }
    },
    onCanvasMouseMove(event) {
      if (this.currElemType === 'WATER_EDGE' && this.currEdgeId) {
        if (this.originX !== -1 && this.originY !== -1) {
          let deletaX = event.clientX - this.originX
          let deletaY = event.clientY - this.originY
          if (Math.abs(deletaX) > 5 && Math.abs(deletaY) > 5) {
            this.originX = -1
            this.originY = -1
            let coorInfo = { visible: false }
            this.$eventManage.fire('canvasMouseMove', { coorInfo })
          }
        }
        return
      }

      if (!this.isPickUpHeightEnable && this.currMode !== 'create') return

      let tipContent = ''

      if (this.isPickUpHeightEnable) {
        tipContent = '左键拾取高度并应用，右键取消'
      } else {
        tipContent = '左键绘制，右键结束'
      }

      let coorInfo = {
        visible: true,
        tipContent,
        clientX: event.clientX,
        clientY: event.clientY
      }
      this.$eventManage.fire('canvasMouseMove', { coorInfo })
    },
    onCanvasMouseLeave() {
      let coorInfo = { visible: false }
      this.$eventManage.fire('canvasMouseMove', { coorInfo })
    },
    toggleSeniorParam() {
      this.isSeniorParamsVisible = !this.isSeniorParamsVisible
      this.setPanelStyle()
    },
    onSeniorParamChange(paramName, paramValue) {
      const Water = window.BlackHole3D.Water
      if (paramName === 'blendDist') {
        Water.setBlendDist(this.waterID, paramValue)
        return
      }
      if (paramName === 'visDist') {
        let visDist = paramValue * 1000
        Water.setVisDist(this.waterID, visDist)
        return
      }
      if (paramName === 'depthBias') {
        Water.setDepthBias(this.waterID, paramValue)
        return
      }
      if (paramName === 'expandDist') {
        Water.setExpandDist(this.waterID, paramValue)
        return
      }
    },
    setPanelStyle() {
      this.$nextTick(() => {
        let domRef = this.$refs.dom.$el
        let panelHeaderDom = domRef.querySelector('.header-box')
        let panelContentDom = domRef.querySelector('.content')
        let height = panelHeaderDom.offsetHeight + panelContentDom.offsetHeight

        domRef.style.height = `${height}px`
        domRef.style.minHeight = `${height}px`
      })
    },
    getWaterDefaultName() {
      let date = new Date()

      let year = date.getFullYear()
      let month = pad2(date.getMonth() + 1)
      let day = pad2(date.getDate())
      let hour = pad2(date.getHours())
      let minute = pad2(date.getMinutes())
      let second = pad2(date.getSeconds())

      let waterName = `水面区域-${year}${month}${day}${hour}${minute}${second}`
      return waterName
    },
    getFolderPathName() {
      let folderPathName = ''

      let { nodeType, dataSetType } = this.currDataSetFolder
      if (dataSetType && dataSetType === 23) {
        // prettier-ignore
        folderPathName = nodeType !== 3 ? this.currDataSetFolder.levelPathName : '水面根目录';
      } else {
        folderPathName = '水面根目录'
      }

      return folderPathName
    },
    getParentId() {
      let parentId

      let dataSetType = this.currDataSetFolder.dataSetType
      if (dataSetType && dataSetType === 23) {
        parentId = this.currDataSetFolder.nodeId
      }

      return parentId
    },
    isSameNameCheck(waterName) {
      return new Promise(resolve => {
        let sceneId = this.$route.query.sceneId
        getSingleSceneTreeById({ sceneId }).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let leafNodes = this.findNodesByNodeType(data, 2)
            let waterList = leafNodes.filter(item => {
              return item.dataSetType === 23
            })
            let find = waterList.find(item => {
              return item.sceneNodeName === waterName
            })

            let isCheckPass = find ? false : true
            resolve(isCheckPass)
          } else {
            this.$message.error(response.errMsg)
          }
        })
      })
    },
    findNodesByNodeType(data, nodeType) {
      const array = []

      const traverse = item => {
        if (item.nodeType === nodeType) {
          array.push(item)
        }
        if (item.subNodes && item.subNodes.length) {
          item.subNodes.forEach(subNode => {
            traverse(subNode)
          })
        }
      }

      data.forEach(item => {
        traverse(item)
      })

      return array
    },
    getStepIndexClassName(step) {
      let className = 'step-index-box'
      if (this.activeSteps.includes(step)) {
        className += ' actived'
      }

      if (this.currStep === step) {
        className += ' active'
      }
      return className
    },
    getStepNameClassName(step) {
      let className = 'step-name-box'
      if (this.currStep === step) {
        className += ' active'
      }
      return className
    },
    // 打开其他面板前给出提示
    openOtherPanelCheck() {
      return new Promise(resolve => {
        let isNameChange = this.originWaterName !== this.waterForm.waterName
        if (this.currMode === '' && !isNameChange) {
          this.closeWaterPanel()
          resolve(true)
        } else {
          // prettier-ignore
          let message = '当前正在创建水面，请先结束创建再执行相关操作';
          let options = {
            message,
            type: 'warning',
            duration: 5000,
            showClose: true
          }
          this.$message(options)
          resolve(false)
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import '@/assets/css/publishSearch.less';

.common-drag-panel {
  left: 6px;
  top: 6px;
  width: 360px;
  height: unset;
  min-width: 360px;

  .create-water-box {
    padding: 8px 15px 5px 15px;

    .steps-box {
      display: flex;
      border-radius: 5px;

      .step-item-box {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        font-size: 14px;

        .step-index-box {
          width: 16px;
          height: 16px;
          font-size: 12px;
          border: 1px solid var(--extrude-step-line);
          border-radius: 50%;
          line-height: 14px;

          &.active.actived {
            color: var(--aside-bg-color);
            border-color: var(--base-primary-color);
            background-color: var(--base-primary-color);
          }

          &.actived {
            color: var(--extrude-step-text-color);
            border-color: var(--extrude-step-line);
          }
        }

        .step-name-box {
          margin-left: 10px;
          color: var(--extrude-step-text-color);

          &.active.actived {
            color: var(--extrude-step-text-color);
          }

          &.active {
            color: var(--base-primary-color);
          }
        }
      }

      .step-connector-line {
        flex-grow: 1;
        margin: auto 10px;
        height: 2px;
        background: var(--extrude-step-line);
      }
    }

    .common-form-box {
      margin-top: 15px;
      margin-left: -15px;

      :deep(.el-form-item) {
        display: flex;

        &.is-error {
          .el-input__inner {
            border-color: var(--error-status-color);
          }
        }

        .el-form-item__label {
          padding: 0px;
        }

        .el-form-item__content {
          flex-grow: 1;

          .el-form-item__error {
            color: var(--error-status-color);
            font-size: 10px;
            padding-top: 2px;
            letter-spacing: 1px;
          }
        }
      }
    }

    .first-step-box {
      margin-top: 15px;

      .create-mode-box {
        display: flex;
        align-items: center;

        .label-box {
          display: flex;
          align-items: center;
          flex-shrink: 0;

          .name {
            font-size: 14px;
          }

          .icon-box {
            margin: 0px 3px;

            span {
              margin-top: 2px;
            }
          }
        }

        .common-button-box {
          display: flex;
          margin-left: 10px;
          flex-grow: 1;

          .read-file-box {
            display: flex;
            flex-grow: 1;
            margin-right: 0px;

            :deep(.file-upload-btn) {
              flex-grow: 1;
              padding-left: 8px;
              padding-right: 8px;
              background-color: var(
                --extrude-texture-fillloadbtn-bgColor
              ) !important;
              border-color: var(--extrude-texture-fillloadbtn-border-color);

              &:hover {
                background-color: var(
                  --extrude-texture-fillloadbtn-hover-bgColor
                ) !important;
                color: var(
                  --extrude-texture-fillloadbtn-hover-text-color
                ) !important;
                border-color: var(
                  --extrude-texture-fillloadbtn-hover-border-color
                ) !important;
              }
            }
          }

          .el-button {
            flex-grow: 1;
            text-align: center;
            border-radius: 4px;
            padding-left: 8px;
            padding-right: 8px;
            :deep(span) {
              display: inline-block;
            }
          }
        }
      }

      .uniform-height-box {
        display: flex;
        margin-top: 15px;
        align-items: center;

        .label-box {
          display: flex;
          align-items: center;
          flex-shrink: 0;

          .name {
            font-size: 14px;
          }

          .icon-box {
            margin: 0px 3px;

            span {
              margin-top: 2px;
            }
          }
        }

        .icon-btn-box {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          margin-left: 10px;
          border-radius: 4px;
          color: var(--extrude-texture-drawbtn-icon-color);
          cursor: pointer;
          background: var(--extrude-texture-drawbtn-bgColor);
          border: 1px solid var(--extrude-texture-drawbtn-boder-color);

          &.active {
            background: var(--extrude-texture-drawbtn-hover-bgColor);
          }

          &:hover {
            background: var(--extrude-texture-drawbtn-hover-bgColor);
          }
        }
      }
    }

    .last-step-box {
      margin: 10px 0px;
      font-size: 14px;

      .basic-param-box {
        display: flex;
        align-items: center;

        .color-box {
          display: flex;
          align-items: center;

          :deep(.color-picker-container) {
            margin-top: 2px;
            text-align: left;

            .picker-box {
              margin-top: 2px;
              border-radius: 3px;

              .pickr {
                .pcr-button {
                  width: 16px;
                  height: 16px;
                  &::before {
                    background: transparent;
                    border-radius: 3px 0px 0px 3px;
                  }
                  &::after {
                    border-radius: 3px 0px 0px 3px;
                  }
                }
              }

              .open-btn-box {
                display: flex;
                background: var(--color-picker-open-btn-bgColor);
                border-radius: 0px 3px 3px 0px;

                i {
                  margin: auto;
                }
              }
            }
          }
        }

        .opacity-box {
          flex-grow: 1;
          display: flex;
          align-items: center;
          margin-left: 10px;

          .opacity-slider-box {
            flex-grow: 1;
            display: flex;
            align-items: center;

            .slider-box {
              flex-grow: 1;
              margin-left: 5px;

              :deep(.el-slider) {
                .el-slider__runway {
                  background: var(--base-bg-light-color81),
                    url('@/assets/img/opacitySlider.svg');

                  .el-slider__bar {
                    background: transparent;
                  }

                  .el-slider__button-wrapper {
                    .el-slider__button {
                      width: 8px;
                      height: 8px;
                      border: 2px solid var(--base-primary-bgColor);
                      background-color: #ffffff;
                    }
                  }
                }
              }
            }

            .slider-value {
              width: 25px;
              flex-shrink: 0;
              margin-left: 10px;
            }
          }
        }
      }

      .senior-param-box {
        margin: 10px 0px;
        text-align: left;
        border-radius: 8px;
        background: var(--water-senior-params-bgColor);
        border: 1px solid var(--water-senior-params-border-bgColor);

        .title-box {
          display: flex;
          align-items: center;
          padding: 10px;
          border-radius: 8px 8px 0px 0px;
          background: var(--water-senior-params-title-bgColor);
          cursor: pointer;

          .icon-box {
            margin-left: 10px;
          }
        }

        .param-content-box {
          padding: 8px 10px;

          .input-item-box {
            display: flex;
            align-items: center;
          }

          .slider-item-box {
            .slider-primary {
              flex-grow: 1;
              padding: 0px 10px;
            }
          }

          .item-box {
            display: flex;
            align-items: center;

            .label-box {
              flex-shrink: 0;
              width: 70px;
              text-align: right;
            }

            .common-input-box {
              flex-grow: 1;
              :deep(.el-input-number--small) {
                width: 100%;

                .el-input__inner {
                  padding: 0px 5px;
                  text-align: left;
                  color: var(--extrude-step-input-color);
                }
              }
            }

            .unit-box {
              margin-left: 3px;
            }
          }
        }
      }
    }

    .folder-path-box {
      display: flex;
      align-items: center;
      margin-top: 20px;
      margin-left: 3px;
      color: var(--input-bg-color);
      font-size: 14px;
    }
  }

  .operation-box {
    border-top: 1px solid var(--extrude-step-footer-border-color);
    padding: 15px;

    .common-button-box {
      display: flex;
      justify-content: end;

      .el-button {
        border-radius: 4px;
        margin-right: 0px;
      }

      .prev-btn {
        margin-right: 10px;
      }
    }
  }
}
</style>
